import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, ChevronUp, Phone, Mail, MapPin } from 'lucide-react'
import { motion } from 'framer-motion'

const Footer = () => {
  const [openSections, setOpenSections] = useState({})

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  const footerSections = [
    {
      title: 'Our Services',
      links: [
        { name: 'Housekeeping', href: '/services/housekeeping' },
        { name: 'Watchman & Security', href: '/services/watchman' },
        { name: 'Ward Boys/Girls', href: '/services/ward-boys' },
        { name: 'Care Takers', href: '/services/caretakers' },
        { name: 'Security Services', href: '/services/security' },
        { name: 'Telecallers', href: '/services/telecallers' },
      ]
    },
    {
      title: 'More Services',
      links: [
        { name: 'Financial Services', href: '/financial-services' },
        { name: 'Accounting Jobs', href: '/services/accounting-jobs' },
        { name: 'Any Degree Jobs', href: '/services/any-degree-jobs' },
        { name: 'Drivers', href: '/services/drivers' },
        { name: 'Delivery Boys', href: '/services/delivery-boys' },
        { name: 'Event & Function Services', href: '/services/event-services' },
      ]
    },
    {
      title: 'Quick Links',
      links: [
        { name: 'About Us', href: '/about' },
        { name: 'All Services', href: '/services' },
        { name: 'Careers', href: '/careers' },
        { name: 'FAQ', href: '/faq' },
        { name: 'Contact Us', href: '/contact' },
      ]
    }
  ]

  return (
    <footer className="bg-white text-black border-t border-gray-200">
      {/* CTA Bar */}
      <div className="bg-gradient-to-r from-primary-700 to-teal-700">
        <div className="container-max py-12 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-3 text-white">Let's Discuss Your Workforce Needs</h2>
          <p className="text-primary-100 mb-6 max-w-xl mx-auto">Our consultants can help you find the best staffing solution for your organization.</p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-white font-semibold py-3 px-8 rounded-md transition-all duration-200 text-lg"
          >
            Get In Touch →
          </Link>
        </div>
      </div>

      <div className="container-max">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Company Info */}
            <div className="lg:col-span-2">
              <Link to="/" className="flex items-center gap-3 mb-6 justify-center lg:justify-start">
                <img
                  src="/shrijaa-logo.png"
                  alt="Shrijaa Outsourcing Consultants"
                  className="h-20 w-auto"
                />
              </Link>
              <p className="text-gray-600 mb-6 leading-relaxed max-w-md">
                Shrijaa Outsourcing Consultants offers comprehensive Workforce Solutions and Financial Services, delivering integrated business support to organizations and individuals through ethical practices, industry expertise, and customer-centric solutions.
              </p>
              <div className="space-y-3 text-black">
                <div className="flex items-start gap-3">
                  <MapPin size={18} className="mt-0.5 text-accent-500 shrink-0" />
                  <a href="https://maps.app.goo.gl/5N4FuTJa3Ej2kv387" target="_blank" rel="noopener noreferrer" className="hover:text-accent-500 transition-colors">BK Guda, SR Nagar, Hyderabad - 500038</a>
                </div>
                <div className="flex items-center gap-3">
                  <Phone size={18} className="text-accent-500 shrink-0" />
                  <a href="tel:+917989308337" className="hover:text-accent-500 transition-colors">+91 7989308337</a>
                </div>
                <div className="flex items-center gap-3">
                  <Mail size={18} className="text-accent-500 shrink-0" />
                  <a href="mailto:info@shrijaaoutsourcing.com" className="hover:text-accent-500 transition-colors">info@shrijaaoutsourcing.com</a>
                </div>
              </div>
            </div>

            {/* Footer Links - Desktop */}
            <div className="lg:col-span-3 hidden lg:grid grid-cols-3 gap-8">
              {footerSections.map((section) => (
                <div key={section.title}>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-accent-500 mb-5">{section.title}</h3>
                  <ul className="space-y-3">
                    {section.links.map((link) => (
                      <li key={link.name}>
                        <Link
                          to={link.href}
                          className="text-gray-700 hover:text-accent-500 transition-colors duration-200 text-sm"
                        >
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Footer Links - Mobile Accordion */}
            <div className="lg:hidden col-span-1 space-y-0">
              {footerSections.map((section) => (
                <div key={section.title} className="border-b border-gray-200">
                  <button
                    onClick={() => toggleSection(section.title)}
                    className="flex items-center justify-between w-full text-left py-4"
                  >
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-accent-500">{section.title}</h3>
                    {openSections[section.title] ? (
                      <ChevronUp size={18} className="text-gray-400" />
                    ) : (
                      <ChevronDown size={18} className="text-gray-400" />
                    )}
                  </button>
                  <motion.div
                    initial={false}
                    animate={{
                      height: openSections[section.title] ? 'auto' : 0,
                      opacity: openSections[section.title] ? 1 : 0
                    }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <ul className="pb-4 space-y-2">
                      {section.links.map((link) => (
                        <li key={link.name}>
                          <Link
                            to={link.href}
                            className="text-gray-700 hover:text-accent-500 transition-colors duration-200 block py-1 text-sm"
                          >
                            {link.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="border-t border-gray-200 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-gray-600 text-sm text-center md:text-left">
              <p>&copy; {new Date().getFullYear()} Shrijaa Outsourcing Consultants. All rights reserved.</p>
            </div>
            <div className="text-gray-500 text-sm">
              <span>
                Designed by{' '}
                <a 
                  href="https://www.octaleads.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-accent-500 hover:text-accent-400 transition-colors"
                >
                  Octaleads Pvt Ltd
                </a>
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
